import React, { useEffect, useState, useCallback } from 'react';
import { Puzzle, Plus, Pencil, Trash2, X, Check, RefreshCw, Wifi, WifiOff, AlertTriangle, Brain, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
const token = () => localStorage.getItem('token');

const REGIONS = [
  'us-east-1','us-east-2','us-west-1','us-west-2',
  'eu-west-1','eu-west-2','eu-central-1',
  'ap-southeast-1','ap-southeast-2','ap-northeast-1',
  'sa-east-1',
];

const PROVIDERS = {
  aws: {
    label: 'Amazon Web Services',
    emoji: '☁️',
    color: '#FF9900',
    fields: [
      { key: 'access_key_id',     label: 'Access Key ID',     type: 'text',     placeholder: 'AKIA...', required: true },
      { key: 'secret_access_key', label: 'Secret Access Key', type: 'password', placeholder: '', required: true },
      { key: 'session_token',     label: 'Session Token',     type: 'password', placeholder: 'Token temporal STS (opcional)', required: false },
    ],
    hasRegion: true,
    regionLabel: 'Región',
  },
  azure: {
    label: 'Microsoft Azure',
    emoji: '🔷',
    color: '#0078d4',
    fields: [
      { key: 'access_key_id',     label: 'Client ID (App Registration)', type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
      { key: 'secret_access_key', label: 'Client Secret',                type: 'password', placeholder: '', required: true },
      { key: 'session_token',     label: 'Tenant ID',                    type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: false },
    ],
    hasRegion: true,
    regionLabel: 'Subscription ID',
  },
  o365: {
    label: 'Microsoft 365',
    emoji: '📧',
    color: '#0078d4',
    fields: [
      { key: 'access_key_id',     label: 'Client ID (App Registration)', type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
      { key: 'secret_access_key', label: 'Client Secret',                type: 'password', placeholder: '', required: true },
      { key: 'session_token',     label: 'Tenant ID',                    type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
    ],
    hasRegion: false,
  },
  gcp: {
    label: 'Google Cloud / Workspace',
    emoji: '🌐',
    color: '#4285F4',
    fields: [
      { key: 'access_key_id',     label: 'Service Account Email',       type: 'text',     placeholder: 'sa@project.iam.gserviceaccount.com', required: true },
      { key: 'secret_access_key', label: 'Service Account Key (JSON)',  type: 'password', placeholder: 'Pegar el JSON completo del service account', required: true },
    ],
    hasRegion: true,
    regionLabel: 'Proyecto ID',
  },
  github: {
    label: 'GitHub',
    emoji: '🐙',
    color: '#24292f',
    fields: [
      { key: 'access_key_id',     label: 'Organización / Usuario', type: 'text',     placeholder: 'mi-organizacion', required: true },
      { key: 'secret_access_key', label: 'Personal Access Token', type: 'password', placeholder: 'ghp_...', required: true },
    ],
    hasRegion: false,
  },
};

async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...(opts.headers || {}) },
  });
  const data = await r.json();
  if ((r.status === 401 || r.status === 403) && data.error?.includes('Token')) {
    window.dispatchEvent(new CustomEvent('itil-session-expired'));
    throw new Error('Sesión expirada. Iniciá sesión nuevamente.');
  }
  if (!r.ok) throw new Error(data.error || `Error ${r.status}`);
  return data;
}

// ── Inline form for create/edit ────────────────────────────────────────────
function AccountForm({ initial, onSave, onCancel }) {
  const isEdit = !!initial;
  const [f, setF] = useState({
    name:              initial?.name        || '',
    provider:          initial?.provider    || 'aws',
    access_key_id:     '',
    secret_access_key: '',
    session_token:     '',
    region:            initial?.region      || 'us-east-1',
    description:       initial?.description || '',
    environment:       initial?.environment || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const prov = PROVIDERS[f.provider] || PROVIDERS.aws;

  const handleSave = async () => {
    if (!f.name.trim()) { setErr('El nombre es requerido'); return; }
    const missingRequired = prov.fields.filter(f2 => f2.required && !f[f2.key]?.trim()).map(f2 => f2.label);
    if (!isEdit && missingRequired.length) {
      setErr(`Campos requeridos: ${missingRequired.join(', ')}`);
      return;
    }
    setSaving(true); setErr('');
    try {
      const body = { name: f.name.trim(), region: f.region, description: f.description.trim(), environment: f.environment || null };
      if (!isEdit) body.provider = f.provider;
      // Always send credential fields — backend keeps existing values if empty
      if (f.access_key_id.trim())     body.access_key_id     = f.access_key_id.trim();
      if (f.secret_access_key.trim()) body.secret_access_key = f.secret_access_key.trim();
      // session_token: send key so backend can clear it if left blank (rotation case)
      body.session_token = f.session_token.trim() || '';
      const result = isEdit
        ? await apiFetch(`/integrations/${initial.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : await apiFetch('/integrations', { method: 'POST', body: JSON.stringify(body) });
      onSave(result);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const inp = {
    width: '100%', boxSizing: 'border-box', padding: '7px 10px',
    border: '1px solid var(--border-2)', borderRadius: 6, fontSize: 13,
    background: 'var(--surface)', color: 'var(--ink)',
  };
  const lbl = (text) => (
    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>{text}</label>
  );

  return (
    <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 10, padding: 20, marginBottom: 16 }}>
      <div style={{ marginBottom: 14 }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>
          {isEdit ? 'Editar cuenta' : 'Nueva cuenta cloud'}
        </span>
      </div>

      {/* Row 1: name, provider, environment, region */}
      <div style={{ display: 'grid', gridTemplateColumns: prov.hasRegion ? '2fr 1fr 1fr 1fr' : '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          {lbl('Nombre *')}
          <input style={inp} value={f.name} onChange={e => set('name', e.target.value)} placeholder="Ej: AWS Producción" />
        </div>
        <div>
          {lbl('Proveedor')}
          <select style={inp} value={f.provider} onChange={e => set('provider', e.target.value)} disabled={isEdit}>
            {Object.entries(PROVIDERS).map(([key, p]) => (
              <option key={key} value={key}>{p.emoji} {p.label}</option>
            ))}
          </select>
        </div>
        <div>
          {lbl('Ambiente')}
          <select style={inp} value={f.environment} onChange={e => set('environment', e.target.value)}>
            <option value="">Sin asignar</option>
            <option value="PROD">PROD</option>
            <option value="TEST">TEST</option>
            <option value="DESA">DESA</option>
            <option value="NETWORKING">NETWORKING</option>
          </select>
        </div>
        {prov.hasRegion && (
          <div>
            {lbl(prov.regionLabel || 'Región')}
            {f.provider === 'aws' ? (
              <select style={inp} value={f.region} onChange={e => set('region', e.target.value)}>
                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            ) : (
              <input style={inp} value={f.region} onChange={e => set('region', e.target.value)}
                placeholder={f.provider === 'azure' ? 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' : 'mi-proyecto-id'} />
            )}
          </div>
        )}
      </div>

      {isEdit && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#0c4a6e' }}>
          Cuenta actual: <strong>{initial?.access_key_hint}</strong>
          {initial?.region ? <> · {initial.region}</> : null}.
          Dejá vacíos los campos que no querés cambiar.
        </div>
      )}

      {/* Dynamic credential fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        {prov.fields.map(field => (
          <div key={field.key}>
            {lbl(isEdit
              ? `${field.label} (vacío = sin cambios)`
              : field.label + (field.required ? ' *' : '')
            )}
            <input
              style={inp}
              type={field.type}
              value={f[field.key]}
              onChange={e => set(field.key, e.target.value)}
              placeholder={isEdit ? '••••••••' : field.placeholder}
              autoComplete="off"
            />
          </div>
        ))}
        <div>
          {lbl('Descripción')}
          <input style={inp} value={f.description} onChange={e => set('description', e.target.value)}
            placeholder="Ej: Cuenta de producción principal" />
        </div>
      </div>

      {err && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleSave} disabled={saving}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 7, padding: '7px 16px', fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          <Check size={14} /> {saving ? 'Guardando…' : 'Guardar'}
        </button>
        <button onClick={onCancel}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg)', color: 'var(--ink-2)', border: 'none', borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          <X size={14} /> Cancelar
        </button>
      </div>
    </div>
  );
}

// ── Account card ───────────────────────────────────────────────────────────
function AccountCard({ account, monitoringId, netId, onEdit, onDelete, onSetMonitoring, onSetNet }) {
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting]       = useState(false);
  const isMonitoring = monitoringId === account.id;
  const isNet        = netId === account.id;

  const provCfg = PROVIDERS[account.provider];

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await apiFetch(`/integrations/${account.id}/test`, { method: 'POST' });
      setTestResult(r);
    } catch (e) { setTestResult({ ok: false, error: e.message }); }
    finally { setTesting(false); }
  };

  const iconBg = provCfg
    ? provCfg.color + '20'
    : 'var(--amber-dim)';
  const iconBorder = provCfg
    ? provCfg.color + '50'
    : 'var(--amber-dim)';

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '14px 18px', background: 'var(--surface)', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 38, height: 38, borderRadius: 8, background: iconBg, border: `1px solid ${iconBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>
          {provCfg?.emoji || '☁️'}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink)' }}>{account.name}</span>
            <span style={{ fontSize: 11, background: 'var(--bg)', color: 'var(--ink-2)', padding: '2px 7px', borderRadius: 5 }}>
              {provCfg?.label || account.provider}{account.region ? ' · ' + account.region : ''}
            </span>
            {account.environment && (() => {
              const ENV_STYLE = {
                PROD: { bg: 'var(--green-dim)', color: 'var(--green)' },
                TEST: { bg: 'var(--accent-dim)', color: 'var(--accent)' },
                DESA: { bg: 'var(--amber-dim)', color: 'var(--accent)' },
              };
              const s = ENV_STYLE[account.environment] || { bg: 'var(--bg)', color: 'var(--ink-2)' };
              return <span style={{ fontSize: 11, color: s.color, fontWeight: 700 }}>{account.environment}</span>;
            })()}
            {isMonitoring && <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>Monitoreo</span>}
            {isNet        && <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>Networking</span>}
            {account.has_session_token && <span style={{ fontSize: 11, background: 'var(--amber-dim)', color: '#854d0e', padding: '2px 7px', borderRadius: 5 }}>Session token activo</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
            {account.access_key_hint}
            {account.description && <> · {account.description}</>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={handleTest} disabled={testing}
            style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--bg)', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--ink-2)', cursor: testing ? 'not-allowed' : 'pointer' }}>
            <RefreshCw size={13} style={testing ? { animation: 'spin 1s linear infinite' } : {}} />
            {testing ? 'Probando…' : 'Test'}
          </button>
          <button onClick={() => onEdit(account)} title="Editar"
            style={{ background: 'var(--bg)', border: 'none', borderRadius: 6, padding: '6px 8px', cursor: 'pointer', color: 'var(--ink-2)' }}>
            <Pencil size={14} />
          </button>
          <button onClick={() => onDelete(account.id)} title="Eliminar"
            style={{ background: 'var(--red-dim)', border: 'none', borderRadius: 6, padding: '6px 8px', cursor: 'pointer', color: 'var(--red)' }}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {testResult && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
          color: testResult.ok ? 'var(--green)' : 'var(--red)',
          background: testResult.ok ? 'var(--green-dim)' : 'var(--red-dim)',
          border: `1px solid ${testResult.ok ? 'var(--green-dim)' : 'var(--red-dim)'}`,
          borderRadius: 6, padding: '6px 12px' }}>
          {testResult.ok ? <Wifi size={13} /> : <WifiOff size={13} />}
          {testResult.ok ? 'Conexión exitosa' : testResult.error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={() => onSetMonitoring(isMonitoring ? null : account.id)}
          style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontWeight: isMonitoring ? 700 : 400,
            border: '1px solid #bfdbfe', background: isMonitoring ? 'var(--accent-dim)' : '#fff', color: isMonitoring ? 'var(--accent)' : 'var(--ink-2)' }}>
          {isMonitoring ? '✓ Cuenta de Monitoreo' : 'Usar para Monitoreo'}
        </button>
        <button onClick={() => onSetNet(isNet ? null : account.id)}
          style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontWeight: isNet ? 700 : 400,
            border: '1px solid var(--green-dim)', background: isNet ? 'var(--green-dim)' : '#fff', color: isNet ? 'var(--green)' : 'var(--ink-2)' }}>
          {isNet ? '✓ Cuenta de Networking' : 'Usar para Networking'}
        </button>
      </div>
    </div>
  );
}

// ── Anthropic (Claude IA) API key ────────────────────────────────────────────
function AnthropicKeyCard() {
  const [aiKey, setAiKey]             = useState('');
  const [configured, setConfigured]  = useState(false);
  const [saving, setSaving]          = useState(false);
  const [msg, setMsg]                = useState(null); // { ok, text }
  const [showKey, setShowKey]        = useState(false);

  useEffect(() => {
    apiFetch('/config')
      .then(cfg => { if (cfg.anthropic_api_key?.value) setConfigured(true); })
      .catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!aiKey.trim()) { setMsg({ ok: false, text: 'Ingresá la API Key' }); return; }
    setSaving(true); setMsg(null);
    try {
      await apiFetch('/config', { method: 'PUT', body: JSON.stringify({ key: 'anthropic_api_key', value: aiKey.trim() }) });
      setConfigured(true);
      setAiKey('');
      setMsg({ ok: true, text: 'API Key guardada correctamente' });
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Error guardando la API Key' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '18px 20px', marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--amber-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Brain size={18} color="var(--accent)" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>Anthropic (Claude IA)</div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>API Key para generar los Informes IA</div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
          background: configured ? 'var(--green-dim)' : 'var(--red-dim)', color: configured ? 'var(--green)' : 'var(--red)',
          border: `1px solid ${configured ? 'var(--green-dim)' : 'var(--red-dim)'}` }}>
          {configured ? 'Configurada' : 'No configurada'}
        </span>
      </div>

      <form onSubmit={save} style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            type={showKey ? 'text' : 'password'}
            value={aiKey}
            onChange={e => setAiKey(e.target.value)}
            placeholder={configured ? 'Ingresá una nueva API Key para reemplazar la actual' : 'sk-ant-api03-...'}
            style={{ width: '100%', fontSize: 13, border: '1px solid var(--border-2)', borderRadius: 8, padding: '9px 36px 9px 12px', color: 'var(--ink)' }}
          />
          <button type="button" onClick={() => setShowKey(s => !s)}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 0, display: 'flex' }}>
            {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <button type="submit" disabled={saving}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? <RefreshCw size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Check size={14} />}
          Guardar
        </button>
      </form>

      {msg && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
          color: msg.ok ? 'var(--green)' : 'var(--red)', background: msg.ok ? 'var(--green-dim)' : 'var(--red-dim)',
          border: `1px solid ${msg.ok ? 'var(--green-dim)' : 'var(--red-dim)'}`, borderRadius: 6, padding: '6px 12px' }}>
          {msg.ok ? <Check size={13} /> : <AlertTriangle size={13} />} {msg.text}
        </div>
      )}

      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10 }}>
        Obtené tu API Key en <strong>console.anthropic.com</strong>. Se almacena de forma segura en la base de datos de tu organización y nunca se muestra completa.
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function Integrations() {
  const [accounts, setAccounts]         = useState([]);
  const [monitoringId, setMonitoringId] = useState(null);
  const [netId, setNetId]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editAccount, setEditAccount]   = useState(null);
  const [error, setError]               = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [accs, cfg] = await Promise.all([
        apiFetch('/integrations'),
        apiFetch('/monitoring/config'),
      ]);
      setAccounts(accs);
      setMonitoringId(cfg.account_id || null);
      setNetId(cfg.net_account_id || null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = (saved) => {
    setAccounts(prev => {
      const idx = prev.findIndex(a => a.id === saved.id);
      return idx >= 0 ? prev.map(a => a.id === saved.id ? saved : a) : [...prev, saved];
    });
    setShowForm(false); setEditAccount(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta cuenta? Esta acción no se puede deshacer.')) return;
    try {
      await apiFetch(`/integrations/${id}`, { method: 'DELETE' });
      setAccounts(prev => prev.filter(a => a.id !== id));
      const updates = {};
      if (monitoringId === id) { updates.account_id = ''; setMonitoringId(null); }
      if (netId === id)        { updates.net_account_id = ''; setNetId(null); }
      if (Object.keys(updates).length)
        await apiFetch('/monitoring/config', { method: 'PUT', body: JSON.stringify(updates) });
    } catch (e) { alert(e.message); }
  };

  const handleSetMonitoring = async (id) => {
    try {
      await apiFetch('/monitoring/config', { method: 'PUT', body: JSON.stringify({ account_id: id || '' }) });
      setMonitoringId(id);
    } catch (e) { alert(e.message); }
  };

  const handleSetNet = async (id) => {
    try {
      await apiFetch('/monitoring/config', { method: 'PUT', body: JSON.stringify({ net_account_id: id || '' }) });
      setNetId(id);
    } catch (e) { alert(e.message); }
  };

  // Group accounts by provider
  const byProvider = Object.keys(PROVIDERS).reduce((acc, key) => {
    acc[key] = accounts.filter(a => a.provider === key);
    return acc;
  }, {});
  // Also collect accounts with unknown providers
  const unknownAccounts = accounts.filter(a => !PROVIDERS[a.provider]);

  const renderCard = (account) => (
    <AccountCard
      key={account.id}
      account={account}
      monitoringId={monitoringId}
      netId={netId}
      onEdit={acc => { setEditAccount(acc); setShowForm(false); }}
      onDelete={handleDelete}
      onSetMonitoring={handleSetMonitoring}
      onSetNet={handleSetNet}
    />
  );

  const allAccounts = [...accounts];

  return (
    <div style={{ padding: '28px 32px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Puzzle size={22} color="var(--accent)" />
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>Integraciones</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>Cuentas cloud y proveedores externos</p>
          </div>
        </div>
        {!showForm && !editAccount && (
          <button onClick={() => { setShowForm(true); setEditAccount(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            <Plus size={15} /> Nueva cuenta cloud
          </button>
        )}
      </div>

      {/* Subtitle */}
      <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 24, marginTop: 4 }}>
        Conectá cuentas de AWS, Azure, Microsoft 365, Google Cloud o GitHub para habilitar monitoreo, inventario y alertas automatizadas.
      </p>

      {/* Anthropic API key (para Informes IA) */}
      <AnthropicKeyCard />

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: 'var(--red)', fontSize: 13 }}>
          <AlertTriangle size={15} /> {error}
        </div>
      )}

      {showForm && <AccountForm onSave={handleSave} onCancel={() => setShowForm(false)} />}
      {editAccount && <AccountForm initial={editAccount} onSave={handleSave} onCancel={() => setEditAccount(null)} />}

      {/* Accounts grouped by provider */}
      {loading ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '20px 0' }}>Cargando cuentas…</div>
      ) : allAccounts.length === 0 ? (
        <div style={{ background: 'var(--bg-subtle)', border: '1px dashed var(--border-2)', borderRadius: 10, padding: '36px 20px', textAlign: 'center', marginBottom: 24 }}>
          <Puzzle size={36} color="var(--border-2)" style={{ marginBottom: 10 }} />
          <p style={{ fontSize: 14, color: 'var(--ink-3)', margin: 0 }}>No hay cuentas configuradas.</p>
          <p style={{ fontSize: 12, color: 'var(--border-2)', margin: '4px 0 0' }}>Agregá una cuenta cloud para habilitar Monitoreo, Costos y Networking.</p>
        </div>
      ) : (
        <>
          {Object.entries(PROVIDERS).map(([provKey, provCfg]) => {
            const provAccounts = byProvider[provKey] || [];
            if (provAccounts.length === 0) return null;
            return (
              <div key={provKey} style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {provCfg.emoji} {provCfg.label}
                  <span style={{ fontWeight: 400, color: 'var(--ink-3)', fontSize: 12 }}>
                    ({provAccounts.length} cuenta{provAccounts.length !== 1 ? 's' : ''})
                  </span>
                </div>
                {provAccounts.map(renderCard)}
              </div>
            );
          })}
          {unknownAccounts.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 10 }}>
                Otros ({unknownAccounts.length})
              </div>
              {unknownAccounts.map(renderCard)}
            </div>
          )}
        </>
      )}

      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '14px 18px', fontSize: 12, color: '#0369a1' }}>
        <strong>¿Cómo funciona?</strong>
        {' '}Cada cuenta se almacena de forma independiente. Asigná una como <em>Monitoreo</em> para que CloudWatch, EC2 y Cost Explorer la usen.
        Si tenés una cuenta separada para VPN/Transit Gateway/Direct Connect, asignala como <em>Networking</em>.
        Para renovar un Session Token (AWS SSO/STS) o rotar credenciales, editá la cuenta y dejá vacíos los campos que no cambian.
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
